import { useState, useCallback, useRef, useEffect } from 'react';
import { Check, Copy as CopyIcon } from 'lucide-react';

/**
 * FE-4: "CopyField exists because the dashboard is full of keys, IDs and URLs.
 * Every one of them is copyable in one click."
 */
async function writeToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* falls through to the legacy path */
  }

  // The Clipboard API needs a secure context; a merchant on plain http in
  // staging should still be able to copy their key.
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;opacity:0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export function useCopy(resetMs = 2000) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(
    async (text) => {
      const ok = await writeToClipboard(String(text ?? ''));
      if (!ok) return false;
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), resetMs);
      return true;
    },
    [resetMs],
  );

  return { copied, copy };
}

export function CopyButton({ value, label = 'Copy', size = 'sm', onCopied, className = '' }) {
  const { copied, copy } = useCopy();

  return (
    <button
      type="button"
      onClick={async () => { if (await copy(value)) onCopied?.(); }}
      aria-label={copied ? 'Copied' : label}
      className={`btn-ghost ${size === 'sm' ? 'btn-sm' : ''} ${className}`}
    >
      {/* FE-14: the confirmation is a word, not only an icon swap. */}
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <CopyIcon className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

/** A labelled, monospaced, one-click-copyable value — keys, ids, URLs. */
export function CopyField({ label, value, hint, monospace = true, className = '' }) {
  return (
    <div className={className}>
      {label && <p className="label">{label}</p>}
      <div className="flex items-stretch gap-2">
        <div
          className={`min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md border px-3 py-2 text-sm text-ink ${
            monospace ? 'font-mono text-xs' : ''
          }`}
          style={{ background: 'rgb(var(--surface-2))', borderColor: 'rgb(var(--line))' }}
        >
          {value || <span className="text-faint">—</span>}
        </div>
        <CopyButton value={value} className="shrink-0" />
      </div>
      {hint && <p className="mt-1.5 text-xs leading-relaxed text-muted">{hint}</p>}
    </div>
  );
}

export default CopyField;
